import React from "react";
import { ModelByIDResponse } from "../types/models";
import { useChatHistoryStore } from "../stores/chatHistoryStore";
import { ReactComponent as ModelIcon } from '../assets/edgerunnerLogo.svg';

interface ModelCardModalProps {
  modelInfo: ModelByIDResponse | null;
  onClose: () => void;
}

export function ModelCardModal({ modelInfo, onClose }: ModelCardModalProps) {
  const selectedModel = useChatHistoryStore((s) => s.selectedModel);
  if (!modelInfo) return null;

  const isActive = modelInfo.id === selectedModel;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[9999] p-4"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-zinc-900 to-zinc-950 text-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-zinc-800/50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900/95 backdrop-blur-md border-b border-zinc-800/50 px-6 py-4 flex items-start justify-between">
          <ModelIcon className='p-1 mr-2 w-10 h-10 flex-shrink-0 flex-grow-0 object-contain'/>
          <div className="flex-1 pr-4">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-xl font-semibold text-neutral-200">
                {modelInfo.misc?.name || modelInfo.id}
              </h2>
              {isActive && (
                <div className="flex items-center gap-2 px-3 py-1">
                  <div className="relative flex items-center justify-center">
                    <span className="absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                  </div>
                  <span className="text-xs font-semibold text-green-400 uppercase tracking-wider">
                    Active
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-red-500 p-2 -mr-2"
            title="Close"
            aria-label="Close"
          >
            <i className="fas fa-times text-xl" />
          </button>
        </div>


        <div className="overflow-y-auto max-h-[calc(90vh-80px)] px-6 py-5 space-y-5">
          {/* Description */}
          {modelInfo.misc?.description && (
            <div className="group">
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 block">
                Description
              </span>
              <div className="bg-zinc-800/50 backdrop-blur-sm px-4 py-3 rounded-lg border border-zinc-700/50 group-hover:border-zinc-600/50 transition-colors">
                <p className="text-neutral-200 leading-relaxed">
                  {modelInfo.misc.description}
                </p>
              </div>
            </div>
          )}

          {/* Model Type */}
          {modelInfo.misc?.type && (
            <div className="group">
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 block">
                Model Type
              </span>
              <div className="flex flex-wrap gap-2">
                {modelInfo.misc.type.map((type: string, index: number) => (
                  <span
                    key={index}
                    className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg shadow-red-900/20 hover:shadow-red-900/40 transition-shadow"
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {modelInfo.misc?.tags && modelInfo.misc.tags.length > 0 && (
            <div className="group">
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 block">
                Tags
              </span>
              <div className="flex flex-wrap gap-2">
                {modelInfo.misc.tags.map((tag: string, index: number) => (
                  <span
                    key={index}
                    className="bg-zinc-800/80 border border-zinc-700/50 text-neutral-300 px-3 py-1.5 rounded-md text-sm hover:bg-zinc-700/80 hover:border-zinc-600/50 transition-colors"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Version */}
            {modelInfo.misc?.version && (
              <div className="group">
                <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 block">
                  Version
                </span>
                <div className="bg-zinc-800/50 backdrop-blur-sm px-4 py-3 rounded-lg border border-zinc-700/50 group-hover:border-zinc-600/50 transition-colors">
                  <p className="text-neutral-200 font-medium">{modelInfo.misc.version}</p>
                </div>
              </div>
            )}

            {/* Owner */}
            <div className="group">
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 block">
                Owned By
              </span>
              <div className="bg-zinc-800/50 backdrop-blur-sm px-4 py-3 rounded-lg border border-zinc-700/50 group-hover:border-zinc-600/50 transition-colors">
                <p className="text-neutral-200 font-medium">{modelInfo.owned_by}</p>
              </div>
            </div>
          </div>

          {/* License */}
          {modelInfo.misc?.license && (
            <div className="group">
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 block">
                License
              </span>
              <div className="bg-zinc-800/50 backdrop-blur-sm px-4 py-3 rounded-lg border border-zinc-700/50 group-hover:border-zinc-600/50 transition-colors">
                <p className="text-neutral-200 font-medium font-mono text-sm">
                  {modelInfo.misc.license}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

